import { DelayedQueueNode, InputNode, InputSourceNode, InputTargetNode, OutputNode, OutputTargetNode, ParkingLotQueueNode, WaitQueueNode } from "@internal/open-catalog";
import { createStyles, ListItem, ListItemIcon, ListItemText, makeStyles, Popper, Theme } from "@material-ui/core";
import { Handle, NodeProps, Position } from "@xyflow/react";
import throttle from "lodash/throttle";
import SnoozeIcon from '@material-ui/icons/Snooze';
import DoubleArrowIcon from '@material-ui/icons/DoubleArrow';
import React, { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { MessagingExchangeIcon, MessagingParkingLotIcon, MessagingQueueIcon, MessagingWaitQueueIcon } from '../../../../../../components/icons';
import { FetchMqStatsService } from "../../../../../../services/FetchMqStatsService";

export const MessagingNodeRefreshInterval = 5000;

export interface MessagingNode {
  id: string;
  data: MessagingNodeData;
  hidden: boolean;
}

export interface MessagingNodeData {
  label: string;
  spec: InputNode | InputSourceNode | InputTargetNode | OutputNode | OutputTargetNode | ParkingLotQueueNode | WaitQueueNode;
  icon: ReactElement,
  type: 'queue' | 'exchange',
  scope: 'internal' | 'external';
  vhost: string;
  refresh: boolean;
  stats: () => Promise<Record<string, any>>;
  link: (baseUrl: string) => string;
}

const throttledExchangeStatsFn = (id: string, stats?: FetchMqStatsService)  => {
  const fn = stats ? stats.exchangeStats.bind(stats) : undefined;
  const throttledStats = fn ? throttle(fn, MessagingNodeRefreshInterval) : undefined;
  return throttledStats ? () => throttledStats(id) : () => Promise.resolve({});
}

const throttledQueueStatsFn = (id: string, stats?: FetchMqStatsService)  => {
  const fn = stats ? stats.queueStats.bind(stats) : undefined;
  const throttledStats = fn ? throttle(fn, MessagingNodeRefreshInterval) : undefined;
  return throttledStats ? () => throttledStats(id) : () => Promise.resolve({});
}

export const buildInputNode = (
  vhost: string,
  node: InputNode,
  refresh: boolean = false,
  statsFn: FetchMqStatsService | undefined
): MessagingNode => {
  return {
    id: node.name, hidden: false, data: {
      label: node.name,
      spec: node,
      icon: <DoubleArrowIcon/>,
      scope: 'internal',
      type: 'exchange',
      vhost,
      refresh,
      stats: throttledExchangeStatsFn(node.name, statsFn),
      link: (baseUrl) => `${baseUrl}/#/exchanges/${vhost}/${node.name}`
    }
  };
};

export const buildInputSourceNode = (
  vhost: string,
  node: InputSourceNode,
  refresh: boolean = false,
  statsFn: FetchMqStatsService | undefined
): MessagingNode => {
  return {
    id: node.name, hidden: false, data: {
      label: node.name,
      spec: node,
      icon: <MessagingExchangeIcon />,
      scope: 'external',
      type: 'exchange',
      vhost,
      refresh,
      stats: throttledExchangeStatsFn(node.name, statsFn),
      link: (baseUrl) => `${baseUrl}/#/exchanges/${vhost}/${node.name}`
    }
  };
};

export const buildOutputNode = (
  vhost: string,
  node: OutputNode,
  refresh: boolean = false,
  statsFn: FetchMqStatsService | undefined
): MessagingNode => {
  return {
    id: node.name, hidden: false, data: {
      label: node.name,
      spec: node,
      icon: <DoubleArrowIcon style={{ transform: 'rotate(180deg)'}}/>,
      scope: 'internal',
      type: 'exchange',
      vhost,
      refresh,
      stats: throttledExchangeStatsFn(node.name, statsFn),
      link: (baseUrl) => `${baseUrl}/#/exchanges/${vhost}/${node.name}`
    }
  };
};

export const buildOutputTargetNode = (
  vhost: string,
  node: OutputTargetNode,
  refresh: boolean = false,
  statsFn: FetchMqStatsService | undefined,
): MessagingNode => {
  return {
    id: node.name, hidden: false, data: {
      label: node.name,
      spec: node,
      icon: <MessagingExchangeIcon />,
      scope: 'external',
      type: 'exchange',
      vhost,
      refresh,
      stats: throttledExchangeStatsFn(node.name, statsFn),
      link: (baseUrl) => `${baseUrl}/#/exchanges/${vhost}/${node.name}`
    }
  };
};

export const buildInputTargetNode = (
  vhost: string,
  node: InputTargetNode,
  refresh: boolean = false,
  statsFn: FetchMqStatsService | undefined
): MessagingNode => {
  return {
    id: node.name, hidden: false, data: {
      label: node.name.substring(node.name.indexOf('.') + 1),
      spec: node,
      icon: <MessagingQueueIcon />,
      scope: 'internal',
      type: 'queue',
      vhost,
      refresh,
      stats: throttledQueueStatsFn(node.name, statsFn),
      link: (baseUrl) => `${baseUrl}/#/queues/${vhost}/${node.name}`
    }
  };
};

export const buildOutputTargetQueueNode = (
  vhost: string,
  node: OutputTargetNode,
  refresh: boolean = false,
  statsFn: FetchMqStatsService | undefined,
  hidden: boolean = false,
): MessagingNode => {
  return {
    id: node.name, hidden, data: {
      label: node.name.substring(node.name.indexOf('.') + 1),
      spec: node,
      icon: <MessagingQueueIcon />,
      scope: 'external',
      type: 'queue',
      vhost,
      refresh,
      stats: throttledQueueStatsFn(node.name, statsFn),
      link: (baseUrl) => `${baseUrl}/#/queues/${vhost}/${node.name}`
    }
  };
};

export const buildWaitQueueNode = (
  vhost: string,
  node: WaitQueueNode,
  refresh: boolean = false,
  statsFn: FetchMqStatsService | undefined,
  hidden: boolean = false,
): MessagingNode => {
  return {
    id: node.name, hidden, data: {
      label: node.name.substring(node.name.indexOf('.') + 1),
      spec: node,
      icon: <MessagingWaitQueueIcon />,
      scope: 'internal',
      type: 'queue',
      vhost,
      refresh,
      stats: throttledQueueStatsFn(node.name, statsFn),
      link: (baseUrl) => `${baseUrl}/#/queues/${vhost}/${node.name}`
    }
  };
};

export const buildParkingLotNode = (
  vhost: string,
  node: ParkingLotQueueNode,
  refresh: boolean = false,
  statsFn: FetchMqStatsService | undefined,
  hidden: boolean = false,
): MessagingNode => {
  return {
    id: node.name, hidden, data: {
      label: node.name.substring(node.name.indexOf('.') + 1),
      spec: node,
      icon: <MessagingParkingLotIcon />,
      scope: 'internal',
      type: 'queue',
      vhost,
      refresh,
      stats: throttledQueueStatsFn(node.name, statsFn),
      link: (baseUrl) => `${baseUrl}/#/queues/${vhost}/${node.name}`
    }
  };
};

export const buildDelayedQueueNode = (
  vhost: string,
  node: DelayedQueueNode,
  refresh: boolean = false,
  statsFn: FetchMqStatsService | undefined,
): MessagingNode => {
  return {
    id: node.name, hidden: false, data: {
      label: node.name,
      spec: node,
      icon: <SnoozeIcon />,
      scope: 'internal',
      type: 'queue',
      vhost,
      refresh,
      stats: throttledQueueStatsFn(node.name, statsFn),
      link: (baseUrl) => `${baseUrl}/#/queues/${vhost}/${node.name}`
    }
  };
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      alignItems: 'center',
      paddingTop: 0,
      paddingBottom: 0,
      paddingRight: '1em'
    },
    icon: {
      minWidth: '35px'
    },
    right: {
      textAlign: 'right'
    },
    internal: {
      backgroundColor: theme.palette.banner.info,
      height: '2em'
    },
    external: {
      backgroundColor: theme.palette.banner.error,
      height: '1em'
    },
    popover: {
      pointerEvents: 'none'
    },
    paper: {
      padding: theme.spacing(1),
      width: '234px'
    },
    statusPopper: {
      color: theme.palette.primary.main
    }
  })
);

export function MessagingNodeType({ data }: NodeProps<any>) {

  const classes = useStyles();
  const handleClassName = data.scope === 'internal' ? classes.internal : classes.external;

  return (
    <ListItem dense button className={classes.root}>
      <ListItemIcon children={data.icon} className={classes.icon} />
      <ListItemText primary={data.label} secondary={data.type} />
      <Handle type="source" position={Position.Right} className={handleClassName} />
      <Handle type="target" position={Position.Left} className={handleClassName} />
    </ListItem>
  );
}

export function MessagingNodeTypeWithStatus({ data, selected }: NodeProps<any>) {

  const classes = useStyles();
  const handleClassName = data.scope === 'internal' ? classes.internal : classes.external;

  const [anchorEl, setAnchorEl] = React.useState(null);

  const [inputSpeed, setInputSpeed] = useState('...');
  const [outputSpeed, setOutputSpeed] = useState('...');

  const ref = useRef<HTMLDivElement>(null);

  const openPopper = (_event: React.MouseEvent<any, MouseEvent>) => {
    setAnchorEl(ref.current as any);
  };

  const closePopper = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl || selected);
  const id = open ? 'simple-popper' : undefined;

  useEffect(() => {
    if (selected && ref.current) {
      setAnchorEl(ref.current as any);
    }
  }, [selected]);

  const refresh = useCallback(() => data.stats().then((stats: any) => {
    setInputSpeed(`${stats.speed.inputRate}/s`);
    setOutputSpeed(`${stats.speed.outputRate}/s`);
  }), [data]);

  useEffect(() => {
    if (open) {
      refresh();
      if (data.refresh) {
        const interval = setInterval(() => refresh(), 5000);
        return () => clearInterval(interval);
      }
    }
    return undefined;
  }, [refresh, data, open]);

  return (
    <ListItem ref={ref} dense button className={classes.root}
      onMouseEnter={openPopper}
      onMouseLeave={closePopper}
    >
      <ListItemIcon children={data.icon} className={classes.icon} />
      <ListItemText primary={data.label} secondary={data.type} />
      <Handle type="source" position={Position.Right} className={handleClassName} />
      <Handle type="target" position={Position.Left} className={handleClassName} />
      {anchorEl !== null && <Popper className={classes.statusPopper} style={{ width: '100%' }} disablePortal id={id} open={open} anchorEl={anchorEl} placement='top-start' modifiers={{
        hide: {
          enabled: false
        },
        preventOverflow: {
          enabled: false,
        },
      }}>
        <ListItem dense disableGutters>
          <ListItemText primary={inputSpeed} />
          <ListItemText style={{ textAlign: 'right' }} primary={outputSpeed} />
        </ListItem>
      </Popper>}
    </ListItem>
  );
}
